import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SquaresFour, List, FunnelSimple, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import ProjectCard from "@/components/ProjectCard";
import ProjectFilters from "@/components/ProjectFilters";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import FilterChips from "@/components/FilterChips";
import { formatINR } from "@/lib/format";

const CHIP_LABELS = {
  q: v => `"${v}"`,
  bhk: v => v.split(",").map(b => `${b === "5" ? "5+" : b} BHK`).join(", "),
  config: v => v.split(",").join(", "),
  construction_status: v => v.split(",").map(s => (s === "ready" ? "RTMI" : s.replace(/_/g, " "))).join(", "),
  city: v => v.replace(/-/g, " "),
  price_min: v => `From ${formatINR(Number(v))}`,
  price_max: v => `Under ${formatINR(Number(v))}`,
  verified: () => "Verified",
  rera_registered: () => "RERA Registered",
  featured: () => "Featured",
  rtmi: () => "RTMI",
};

export default function Projects({ fixedStatus }) {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [layout, setLayout] = useState("grid");

  const params = Object.fromEntries(sp.entries());
  const [keyword, setKeyword] = useState(params.q || "");

  useEffect(() => {
    setLoading(true);
    const qp = new URLSearchParams(sp);
    if (qp.get("category") === "commercial") qp.delete("bhk"); // BHK never applies to commercial
    else qp.delete("config");
    if (fixedStatus) qp.set("construction_status", fixedStatus); // dedicated pages (e.g. New Launch) lock the status filter
    qp.set("page_size", "60");
    api.get(`/projects?${qp.toString()}`)
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [sp]);

  const update = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value === "" || value === undefined || value === null) next.delete(key);
    else next.set(key, value);
    setSp(next);
  };

  const search = () => update("q", keyword.trim());
  const clearAll = () => setSp(params.category ? { category: params.category } : {});

  const filters = <ProjectFilters params={params} update={update} keyword={keyword} setKeyword={setKeyword} search={search} clearAll={clearAll} />;

  return (
    <div>
      <div className="section-blue py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            {params.category === "commercial" ? (
              <>
                <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-3">Premium Commercial Real Estate Opportunities</div>
                <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mb-3">Commercial Projects</h1>
                <p className="text-slate-600 max-w-2xl">Explore premium commercial projects, business spaces and investment opportunities across prime locations, carefully selected for businesses and investors.</p>
              </>
            ) : params.category === "residential" && !fixedStatus ? (
              <>
                <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-3">Premium Residential Developments</div>
                <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mb-3">Residential Projects</h1>
                <p className="text-slate-600 max-w-2xl">Explore premium residential projects from trusted builders across prime locations — RERA verified homes for every budget.</p>
              </>
            ) : (
              <>
                <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-3">New Launches</div>
                <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mb-3">New Launch Projects</h1>
                <p className="text-slate-600 max-w-2xl">Handpicked new-launch developments from India's most trusted builders — RERA verified, on-time delivery.</p>
              </>
            )}
          </div>
          <div className="flex bg-white border border-slate-300 rounded-lg overflow-hidden" data-testid="project-layout-toggle">
            {[["grid", SquaresFour, ""], ["list", List, "hidden md:inline-flex"]].map(([k, Icon, extra]) => (
              <button key={k} data-testid={`project-layout-${k}`} onClick={() => setLayout(k)}
                className={`p-2.5 ${extra} ${layout === k ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-blue-50 hover:text-blue-600"} transition-colors`} aria-label={`${k} view`}>
                <Icon size={18} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="text-sm text-slate-600" data-testid="project-count">{loading ? "Searching…" : `${total} project${total === 1 ? "" : "s"} found`}</div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Mobile: filters open in a NON-MODAL drawer — a modal overlay + in-drawer dropdowns
                can deadlock Radix's body pointer lock (page freeze). modal={false} avoids that. */}
            <Sheet modal={false}>
              <SheetTrigger asChild>
                <button data-testid="project-filters-btn" className="lg:hidden flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 hover:border-blue-300 hover:text-blue-600 transition-colors"><FunnelSimple size={16} /> Filters</button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 sm:w-96 overflow-y-auto bg-white [&>button]:hidden">
                <div className="mt-6 mb-4 flex justify-end">
                  <SheetClose asChild>
                    <button type="button" data-testid="project-filters-close" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-blue-700"><X size={14} weight="bold" /> Close</button>
                  </SheetClose>
                </div>
                {filters}
              </SheetContent>
            </Sheet>
            <Select value={params.sort || "newest"} onValueChange={v => update("sort", v === "newest" ? "" : v)}>
              <SelectTrigger data-testid="pf-sort" className="w-56 bg-white border-slate-300 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Recently Added First</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-8 items-start">
          {/* Desktop: fixed left filter sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0 card-premium p-5 sticky top-24" data-testid="projects-filter-sidebar">
            {filters}
          </aside>

          <div className="flex-1 min-w-0">
            <FilterChips params={params} update={update} exclude={["category"]} labels={CHIP_LABELS} />

            {layout === "list" ? (
              <>
                <div className="space-y-4 pb-12 hidden md:block">{items.map(p => <ProjectCard key={p.id} p={p} layout="list" />)}</div>
                <div className="grid grid-cols-1 gap-6 pb-12 md:hidden">{items.map(p => <ProjectCard key={p.id} p={p} />)}</div>
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-12">{items.map(p => <ProjectCard key={p.id} p={p} />)}</div>
            )}
            {loading && <div className="text-center py-10 text-slate-500">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="text-center py-20 text-slate-500 bg-blue-50 rounded-xl border border-blue-100 mb-12" data-testid="projects-empty">No projects match your filters. Try adjusting them.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
